import React, { useState, useEffect } from "react";
import { X, Cookie, Shield, ChevronRight, Check } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { getCookieConsentLabels } from "../data/cookieConsentI18n";
import { COOKIE_CONSENT_CHANGED_EVENT, OPEN_COOKIE_SETTINGS_EVENT } from "../hooks/useCookieConsent";
import { disableGA4, ensureGA4Loaded } from "../services/analyticsService";

// ─────────────────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    gtag?: Function | undefined;
    dataLayer?: any[] | undefined;
    fbq?: Function | undefined;
  }
}

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cookie utilities
const setCookie = (name: string, value: string, days: number): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${secure}`;
};

const removeCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

const removeCookiePrefix = (prefix: string): void => {
  document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0] ?? "")
    .filter((name) => name.startsWith(prefix))
    .forEach(removeCookie);
};

const generateSessionId = (): string =>
  `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateCSRFToken = (): string =>
  Math.random().toString(36).substr(2) + Math.random().toString(36).substr(2);

// ─────────────────────────────────────────────────────────────────────────────
const initializeGoogleAnalytics = (): void => {
  ensureGA4Loaded();
};

const initializeFacebookPixel = (): void => {
  const pixelId = process.env['VITE_META_PIXEL_ID'];
  if (!pixelId || window.fbq) return;
  const script = document.createElement("script");
  script.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
  document.head.appendChild(script);
};

const removeTrackingScripts = (): void => {
  disableGA4();
  document.querySelectorAll('script[src*="googletagmanager"]').forEach((s) => s.remove());
  document.querySelectorAll('script[src*="facebook.net"]').forEach((s) => s.remove());
  window.gtag = undefined;
  window.dataLayer = undefined;
  window.fbq = undefined;
};

const initializeCookies = (preferences: CookiePreferences, language: string): void => {
  removeCookie("_ga"); removeCookie("_gid"); removeCookie("_gat"); removeCookiePrefix("_ga_");
  removeCookie("_fbp"); removeCookie("fbp"); removeCookie("fr");
  setCookie("session_id", generateSessionId(), 30);
  setCookie("csrf_token", generateCSRFToken(), 1);
  setCookie("language", language, 365);
  setCookie("cookie_consent", JSON.stringify(preferences), 365);
  if (preferences.analytics) {
    initializeGoogleAnalytics();
  } else {
    disableGA4();
    window.gtag = undefined;
    window.dataLayer = undefined;
  }
  if (preferences.marketing) {
    initializeFacebookPixel();
  } else {
    document.querySelectorAll('script[src*="facebook.net"]').forEach((s) => s.remove());
    window.fbq = undefined;
  }
  if (preferences.personalization) {
    setCookie("user_theme", "light", 365);
    setCookie("user_preferences", JSON.stringify({ fontSize: "medium", reducedMotion: false }), 365);
  } else {
    removeCookie("user_theme"); removeCookie("user_preferences");
  }
  if (!preferences.analytics && !preferences.marketing) removeTrackingScripts();
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT));
};

// ─────────────────────────────────────────────────────────────────────────────
// Toggle switch
const Toggle: React.FC<{ checked: boolean; onChange: () => void; disabled?: boolean }> = ({
  checked, onChange, disabled,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${
      disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
    } ${checked ? "bg-blue-500" : "bg-slate-600"}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Main component
const CookieConsent: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const labels = getCookieConsentLabels(currentLanguage);
  const cookieTypes = (["necessary", "analytics", "marketing", "personalization"] as const).map((key) => ({
    key,
    label: labels.types[key].label,
    desc: labels.types[key].description,
    locked: key === "necessary",
  }));
  const [isVisible,     setIsVisible]     = useState(false);
  const [showSettings,  setShowSettings]  = useState(false);
  const [leaving,       setLeaving]       = useState(false);
  const [isMounted,     setIsMounted]     = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({
    necessary: true, analytics: false, marketing: false, personalization: false,
  });

  useEffect(() => {
    setIsMounted(true);
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      const t = setTimeout(() => setIsVisible(true), 1200);
      return () => clearTimeout(t);
    } else {
      const saved = localStorage.getItem("cookiePreferences");
      if (saved) initializeCookies(JSON.parse(saved), currentLanguage);
      return undefined;
    }
  }, [currentLanguage]);

  useEffect(() => {
    const reopen = () => {
      const saved = localStorage.getItem("cookiePreferences");
      if (saved) {
        try {
          setPrefs({ ...JSON.parse(saved), necessary: true });
        } catch {
          // ignore malformed storage, keep current toggle state
        }
      }
      setLeaving(false);
      setShowSettings(true);
      setIsVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  const dismiss = (callback: () => void) => {
    setLeaving(true);
    setTimeout(() => { callback(); setIsVisible(false); setLeaving(false); }, 280);
  };

  const acceptAll = () => {
    const p: CookiePreferences = { necessary: true, analytics: true, marketing: true, personalization: true };
    localStorage.setItem("cookieConsent", "all");
    localStorage.setItem("cookiePreferences", JSON.stringify(p));
    initializeCookies(p, currentLanguage);
    dismiss(() => {});
  };

  const acceptNecessary = () => {
    const p: CookiePreferences = { necessary: true, analytics: false, marketing: false, personalization: false };
    localStorage.setItem("cookieConsent", "necessary");
    localStorage.setItem("cookiePreferences", JSON.stringify(p));
    initializeCookies(p, currentLanguage);
    dismiss(() => {});
  };

  const savePrefs = () => {
    localStorage.setItem("cookieConsent", "custom");
    localStorage.setItem("cookiePreferences", JSON.stringify(prefs));
    initializeCookies(prefs, currentLanguage);
    dismiss(() => setShowSettings(false));
  };

  if (!isMounted || !isVisible) return null;

  return (
    <div
      className={`fixed bottom-4 right-4 z-[9990] w-[340px] max-w-[calc(100vw-2rem)] transition-all duration-300 ease-out ${
        leaving
          ? "opacity-0 translate-y-4 scale-95"
          : "opacity-100 translate-y-0 scale-100"
      }`}
      role="dialog"
      aria-label={labels.title}
      aria-modal="false"
    >
      {/* Card */}
      <div className="relative bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden">

        {/* top accent bar */}
        <div className="h-[3px] w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        {!showSettings ? (
          /* ── Banner view ───────────────────────────────────────── */
          <div className="p-5">
            {/* Header row */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm leading-none">{labels.title}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{labels.subtitle}</p>
                </div>
              </div>
              <button
                onClick={() => dismiss(() => {})}
                className="text-slate-500 hover:text-slate-300 transition-colors p-1 rounded-lg hover:bg-white/5 -mt-0.5 -mr-0.5"
                aria-label={labels.dismiss}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <p className="text-slate-400 text-xs leading-relaxed mb-4">
              {labels.description}{" "}
              {/* Opens in a new tab — a plain <a> here would be a full-page navigation,
                  unmounting the page underneath this banner (e.g. the home page's
                  assessment intake form) and discarding any unsaved form state. */}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
              >
                {labels.privacyPolicy}
              </a>
            </p>

            {/* Action buttons */}
            <button
              onClick={acceptAll}
              className="w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-semibold py-2.5 rounded-xl transition-all duration-200 mb-2"
            >
              <Check className="w-3.5 h-3.5" />
              {labels.acceptAll}
              <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-60" />
            </button>

            <div className="flex gap-2">
              <button
                onClick={() => setShowSettings(true)}
                className="flex-1 text-slate-400 hover:text-slate-200 text-xs font-medium py-2 rounded-xl border border-slate-700 hover:border-slate-600 hover:bg-white/5 transition-all"
              >
                {labels.customise}
              </button>
              <button
                onClick={acceptNecessary}
                className="flex-1 text-slate-400 hover:text-slate-200 text-xs font-medium py-2 rounded-xl border border-slate-700 hover:border-slate-600 hover:bg-white/5 transition-all"
              >
                {labels.essentialOnly}
              </button>
            </div>
          </div>
        ) : (
          /* ── Settings view ─────────────────────────────────────── */
          <div className="p-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-400" />
                <span className="text-white font-bold text-sm">{labels.settings}</span>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="text-slate-500 hover:text-slate-300 transition-colors p-1 rounded-lg hover:bg-white/5"
                aria-label={labels.back}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Toggle list */}
            <div className="space-y-2.5 mb-4">
              {cookieTypes.map(({ key, label, desc, locked }) => (
                <div key={key} className="flex items-start gap-3 py-1">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-200 text-xs font-semibold">{label}</p>
                    <p className="text-slate-500 text-[11px] leading-tight mt-0.5">{desc}</p>
                  </div>
                  {locked ? (
                    <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-full px-2 py-0.5 flex-shrink-0 mt-0.5">
                      {labels.alwaysOn}
                    </span>
                  ) : (
                    <div className="flex-shrink-0 mt-0.5">
                      <Toggle
                        checked={prefs[key]}
                        onChange={() => setPrefs((p) => ({ ...p, [key]: !p[key] }))}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="h-px bg-slate-800 mb-4" />

            {/* Save */}
            <button
              onClick={savePrefs}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-semibold py-2.5 rounded-xl transition-all duration-200"
            >
              {labels.save}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
